import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/hooks/useLanguage';
import { getLocalizedPagePath, isLocalizedSeoPage, pagePathMap, type SeoPage } from '@/lib/publicRoutes';

interface SEOHeadProps {
  page?: SeoPage;
}

const seoContent = {
  fr: {
    home: {
      title: "OuraTrust | Vos avis Google travaillent pour vous. Ou contre vous.",
      description: "Débordé, mal noté ou simplement convaincu que vous pouvez faire mieux ? OuraTrust centralise vos avis, vous aide à répondre en secondes grâce à l'IA et vous redonne le contrôle. Sans expertise. Sans complexité.",
      breadcrumbName: "Accueil",
    },
    login: {
      title: "Connexion | OuraTrust",
      description: "Connectez-vous à votre espace OuraTrust pour gérer vos avis clients et votre e-réputation.",
      breadcrumbName: "Connexion",
    },
    signup: {
      title: "Inscription | OuraTrust - Essai Gratuit 14 jours",
      description: "Créez votre compte OuraTrust et pilotez vos avis, votre fiche Google et votre visibilité locale. Essai gratuit de 14 jours.",
      breadcrumbName: "Inscription",
    },
    privacy: {
      title: "Politique de Confidentialité | OuraTrust",
      description: "Découvrez comment OuraTrust protège vos données personnelles conformément au RGPD et à la Google API Services User Data Policy.",
      breadcrumbName: "Politique de Confidentialité",
    },
    terms: {
      title: "Conditions Générales d'Utilisation | OuraTrust",
      description: "Un cadre contractuel clair pour comprendre les règles d'usage, les responsabilités et les limites du service OuraTrust.",
      breadcrumbName: "Conditions d'Utilisation",
    },
    legal: {
      title: "Mentions Légales | OuraTrust",
      description: "Informations légales et mentions obligatoires du site OuraTrust – Apex Digital Flow LLC.",
      breadcrumbName: "Mentions Légales",
    },
    contact: {
      title: "Contact | OuraTrust – Parlons de votre réputation locale",
      description: "Contactez l'équipe OuraTrust pour poser vos questions, découvrir la plateforme ou voir comment mieux gérer vos avis et votre visibilité locale.",
      breadcrumbName: "Contact",
    },
    dashboard: {
      title: "Tableau de Bord | OuraTrust",
      description: "Gérez vos avis clients depuis votre tableau de bord OuraTrust.",
      breadcrumbName: "Tableau de Bord",
    },
    'how-it-works': {
      title: "Comment ça marche | OuraTrust – Gérez vos avis simplement",
      description: "Découvrez comment OuraTrust vous aide à centraliser vos avis, répondre plus vite avec l'IA et piloter votre réputation locale dans une interface simple.",
      breadcrumbName: "Comment ça marche",
    },
    pricing: {
      title: "Tarifs | OuraTrust – Starter, Pro et Business",
      description: "Choisissez le plan OuraTrust adapté à votre activité. Centralisez vos avis, répondez plus vite avec l'IA et pilotez votre visibilité locale simplement.",
      breadcrumbName: "Tarifs",
    },
    about: {
      title: "À propos | OuraTrust – Une plateforme simple pour mieux gérer vos avis",
      description: "Découvrez pourquoi OuraTrust a été créé : aider les entreprises locales à mieux gérer leurs avis, leur réputation et leur visibilité locale avec un outil simple et sérieux.",
      breadcrumbName: "À Propos",
    },
    'data-deletion': {
      title: "Suppression des Données | OuraTrust",
      description: "Demandez la suppression de vos données OuraTrust. Procédure conforme RGPD et CCPA.",
      breadcrumbName: "Suppression des Données",
    },
    blog: {
      title: "OuraTrust Insights | Blog Expert Réputation en Ligne",
      description: "Analyses et méthodes pour piloter avis, présence locale et visibilité locale.",
      breadcrumbName: "Blog",
    },
    'google-data-usage': {
      title: "Utilisation des Données Google | OuraTrust",
      description: "Découvrez comment OuraTrust utilise vos données Google conformément à la Google API Services User Data Policy (Limited Use).",
      breadcrumbName: "Utilisation des Données Google",
    },
  },
  en: {
    home: {
      title: "OuraTrust | Your Google reviews work for you. Or against you.",
      description: "Overwhelmed, poorly rated, or simply convinced you could do better? OuraTrust centralizes your reviews, helps you reply in seconds with AI, and gives you back control. No expertise. No complexity.",
      breadcrumbName: "Home",
    },
    login: {
      title: "Login | OuraTrust",
      description: "Sign in to your OuraTrust dashboard to manage customer reviews and your online reputation.",
      breadcrumbName: "Login",
    },
    signup: {
      title: "Sign Up | OuraTrust - 14-Day Free Trial",
      description: "Create your OuraTrust account and manage reviews, Google profile and local visibility. 14-day free trial included.",
      breadcrumbName: "Sign Up",
    },
    privacy: {
      title: "Privacy Policy | OuraTrust",
      description: "Learn how OuraTrust protects your personal data in compliance with GDPR and Google API Services User Data Policy.",
      breadcrumbName: "Privacy Policy",
    },
    terms: {
      title: "Terms of Use | OuraTrust",
      description: "A clear contractual framework to understand the rules of use, responsibilities, and limits of the OuraTrust service.",
      breadcrumbName: "Terms of Service",
    },
    legal: {
      title: "Legal Notice | OuraTrust",
      description: "Legal information and mandatory notices for the OuraTrust website – Apex Digital Flow LLC.",
      breadcrumbName: "Legal Notice",
    },
    contact: {
      title: "Contact | OuraTrust – Let's talk about your local reputation",
      description: "Contact the OuraTrust team to ask questions, discover the platform, or see how to better manage your reviews and local visibility.",
      breadcrumbName: "Contact",
    },
    dashboard: {
      title: "Dashboard | OuraTrust",
      description: "Manage your customer reviews from your OuraTrust dashboard.",
      breadcrumbName: "Dashboard",
    },
    'how-it-works': {
      title: "How it works | OuraTrust – Manage your reviews simply",
      description: "See how OuraTrust helps you centralize reviews, reply faster with AI, and manage your local reputation in one simple interface.",
      breadcrumbName: "How It Works",
    },
    pricing: {
      title: "Pricing | OuraTrust – Starter, Pro and Business",
      description: "Choose the OuraTrust plan that fits your business. Centralize reviews, reply faster with AI, and manage your local visibility simply.",
      breadcrumbName: "Pricing",
    },
    about: {
      title: "About | OuraTrust – A simple platform to manage reviews better",
      description: "Learn why OuraTrust was created: to help local businesses better manage reviews, reputation, and local visibility with a simple and serious tool.",
      breadcrumbName: "About",
    },
    'data-deletion': {
      title: "Data Deletion | OuraTrust",
      description: "Request deletion of your OuraTrust data. GDPR and CCPA compliant procedure.",
      breadcrumbName: "Data Deletion",
    },
    blog: {
      title: "OuraTrust Insights | Expert Online Reputation Blog",
      description: "Analysis and methods for managing reviews, local presence and visibility.",
      breadcrumbName: "Blog",
    },
    'google-data-usage': {
      title: "Google Data Usage | OuraTrust",
      description: "Learn how OuraTrust uses your Google data in compliance with the Google API Services User Data Policy (Limited Use).",
      breadcrumbName: "Google Data Usage",
    },
  }
};

function buildBreadcrumbJsonLd(page: SeoPage, baseUrl: string, language: 'fr' | 'en') {
  const content = seoContent[language][page as keyof typeof seoContent['fr']];
  if (!content) return null;

  const homeName = language === 'fr' ? 'Accueil' : 'Home';
  const items: Array<{ '@type': string; position: number; name: string; item?: string }> = [
    {
      '@type': 'ListItem',
      position: 1,
      name: homeName,
      item: `${baseUrl}${getLocalizedPagePath('home', language)}`,
    },
  ];

  if (page !== 'home') {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: content.breadcrumbName,
      item: `${baseUrl}${getLocalizedPagePath(page, language)}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

export function SEOHead({ page = 'home' }: SEOHeadProps) {
  const { language } = useLanguage();
  const content = seoContent[language][page] || seoContent[language].home;
  const baseUrl = 'https://ouratrust.com';
  const canonicalPath = getLocalizedPagePath(page, language);
  const canonicalUrl = `${baseUrl}${canonicalPath}`;
  const frUrl = `${baseUrl}${isLocalizedSeoPage(page) ? getLocalizedPagePath(page, 'fr') : pagePathMap[page]}`;
  const enUrl = `${baseUrl}${isLocalizedSeoPage(page) ? getLocalizedPagePath(page, 'en') : pagePathMap[page]}`;
  const breadcrumbJsonLd = buildBreadcrumbJsonLd(page, baseUrl, language);

  return (
    <Helmet>
      <html lang={language} />
      <title>{content.title}</title>
      <meta name="description" content={content.description} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="fr-FR" href={frUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={frUrl} />
      <meta property="og:title" content={content.title} />
      <meta property="og:description" content={content.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="OuraTrust" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={`${baseUrl}/og-image.png`} />
      <meta property="og:locale" content={language === 'fr' ? 'fr_FR' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'fr' ? 'en_US' : 'fr_FR'} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={content.title} />
      <meta name="twitter:description" content={content.description} />
      <meta name="twitter:image" content={`${baseUrl}/og-image.png`} />
      {breadcrumbJsonLd ? (
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
